import React, { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import {
  listConsultants,
  createConsultant,
  updateConsultant,
  setConsultantActive,
  deleteConsultant,
} from '../../services/adminApi';
import { LANGUAGE_LABELS } from '../../utils/languages';
import '../../styles/consultantsPanel.scss';

const EMPTY_FORM = { name: '', calendar_id: '', type: 'virtual', language: 'spanish' };

// Etiquetas legibles para el badge de tipo ('both' => "Ambos").
const TYPE_LABELS = { virtual: 'Virtual', presencial: 'Presencial', both: 'Ambos' };

const ConsultantsPanel = () => {
  const { session } = useContext(AuthContext);
  const token = session?.access_token;

  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  // Formulario de alta.
  const [form, setForm] = useState(EMPTY_FORM);
  const [createBusy, setCreateBusy] = useState(false);
  const [createMsg, setCreateMsg] = useState('');
  const [createErr, setCreateErr] = useState('');

  // Edición inline.
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const data = await listConsultants(token);
      setConsultants(data.consultants || []);
    } catch (err) {
      setError(err.message || 'No se pudo cargar la lista de consultores.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateMsg('');
    setCreateErr('');
    setCreateBusy(true);
    try {
      await createConsultant(token, {
        name: form.name.trim(),
        calendar_id: form.calendar_id.trim(),
        type: form.type,
        language: form.language,
      });
      setCreateMsg(`Consultor ${form.name.trim()} agregado.`);
      setForm(EMPTY_FORM);
      await refresh();
    } catch (err) {
      setCreateErr(err.message);
    } finally {
      setCreateBusy(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditForm({
      name: c.name,
      calendar_id: c.calendar_id,
      type: c.type,
      language: c.language || 'spanish',
    });
    setError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
  };

  const handleSaveEdit = async (id) => {
    setBusyId(id);
    setError('');
    try {
      await updateConsultant(token, id, {
        name: editForm.name.trim(),
        calendar_id: editForm.calendar_id.trim(),
        type: editForm.type,
        language: editForm.language,
      });
      cancelEdit();
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleActive = async (c) => {
    setBusyId(c.id);
    setError('');
    try {
      await setConsultantActive(token, c.id, !c.active);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`¿Eliminar a ${c.name}? Esta acción no se puede deshacer.`)) return;
    setBusyId(c.id);
    setError('');
    try {
      await deleteConsultant(token, c.id);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="consultants-panel">
      <h1>Consultores</h1>

      <section className="consultants-form-section">
        <h2>Agregar consultor</h2>
        <form className="consultants-form" onSubmit={handleCreate}>
          <input
            type="text"
            placeholder="Nombre"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <input
            type="text"
            placeholder="Calendar ID (correo o ...@group.calendar.google.com)"
            value={form.calendar_id}
            onChange={(e) => setForm((f) => ({ ...f, calendar_id: e.target.value }))}
            required
          />
          <select
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
          >
            <option value="virtual">Virtual</option>
            <option value="presencial">Presencial</option>
            <option value="both">Ambos</option>
          </select>
          <select
            value={form.language}
            onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}
          >
            <option value="spanish">{LANGUAGE_LABELS.spanish}</option>
            <option value="english">{LANGUAGE_LABELS.english}</option>
            <option value="both">{LANGUAGE_LABELS.both}</option>
          </select>
          <button
            type="submit"
            className="primary"
            disabled={createBusy || !form.name || !form.calendar_id}
          >
            {createBusy ? '...' : 'Agregar'}
          </button>
        </form>
        {createMsg && <p className="consultants-notice">{createMsg}</p>}
        {createErr && <p className="consultants-error">{createErr}</p>}
      </section>

      <section className="consultants-list">
        <div className="consultants-list-header">
          <h2>Consultores</h2>
          <button type="button" onClick={refresh} disabled={loading}>
            {loading ? 'Cargando...' : 'Refrescar'}
          </button>
        </div>

        {error && <p className="consultants-error">{error}</p>}

        <table className="consultants-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Calendar ID</th>
              <th>Tipo</th>
              <th>Idioma</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {consultants.map((c) => {
              const isEditing = editingId === c.id;
              const isBusy = busyId === c.id;
              return (
                <tr key={c.id} className={c.active ? '' : 'is-disabled'}>
                  <td>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                      />
                    ) : (
                      c.name
                    )}
                  </td>
                  <td className="calendar-id">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.calendar_id}
                        onChange={(e) =>
                          setEditForm((f) => ({ ...f, calendar_id: e.target.value }))
                        }
                      />
                    ) : (
                      c.calendar_id
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <select
                        value={editForm.type}
                        onChange={(e) => setEditForm((f) => ({ ...f, type: e.target.value }))}
                      >
                        <option value="virtual">Virtual</option>
                        <option value="presencial">Presencial</option>
                        <option value="both">Ambos</option>
                      </select>
                    ) : (
                      <span className={`type-badge ${c.type}`}>{TYPE_LABELS[c.type] || c.type}</span>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <select
                        value={editForm.language}
                        onChange={(e) => setEditForm((f) => ({ ...f, language: e.target.value }))}
                      >
                        <option value="spanish">{LANGUAGE_LABELS.spanish}</option>
                        <option value="english">{LANGUAGE_LABELS.english}</option>
                        <option value="both">{LANGUAGE_LABELS.both}</option>
                      </select>
                    ) : (
                      <span className={`lang-badge ${c.language || 'spanish'}`}>
                        {LANGUAGE_LABELS[c.language] || LANGUAGE_LABELS.spanish}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${c.active ? 'on' : 'off'}`}>
                      {c.active ? 'Activo' : 'Desactivado'}
                    </span>
                  </td>
                  <td className="actions">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          className="reactivate"
                          onClick={() => handleSaveEdit(c.id)}
                          disabled={isBusy || !editForm.name || !editForm.calendar_id}
                        >
                          Guardar
                        </button>
                        <button type="button" onClick={cancelEdit} disabled={isBusy}>
                          Cancelar
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => startEdit(c)} disabled={isBusy}>
                          Editar
                        </button>
                        <button
                          type="button"
                          className={c.active ? 'deactivate' : 'reactivate'}
                          onClick={() => handleToggleActive(c)}
                          disabled={isBusy}
                        >
                          {c.active ? 'Desactivar' : 'Reactivar'}
                        </button>
                        <button
                          type="button"
                          className="danger"
                          onClick={() => handleDelete(c)}
                          disabled={isBusy}
                        >
                          Eliminar
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && consultants.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">No hay consultores.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default ConsultantsPanel;
